import React, { useEffect, useState, useMemo } from 'react';
import { Select, Spin, Empty } from 'antd';
import { Pie } from '@ant-design/plots';
import { useSelector } from 'react-redux';
import { fetchQuotationMetrics } from '../vendor/vendor-quotations/quotationService';
import './QuotationMetricsNudge.css';

const PERIOD_OPTIONS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'all', label: 'All Time' },
];

const STATUS_COLOR_MAP = {
  draft: '#ff4d4f',
  ready: '#1890FF',
  pending: '#FAAD14',
  active: '#52C41A',
  inactive: '#FF4D4F',
  completed: '#722ED1',
};

const formatStatus = (status) => status.charAt(0).toUpperCase() + status.slice(1);

const ACCENT_COLOR = '#4800FF';

const buildPieTooltip = (statusColorMap) => ({
  tooltip: {
    title: false,
    items: [
      (datum) => ({
        name: datum.status,
        value: datum.count,
        color: statusColorMap[datum.status?.toLowerCase()] || '#8C8C8C',
      }),
    ],
  },
  interaction: {
    elementHighlight: true,
    tooltip: {
      render: (_event, { items }) => {
        const item = items?.[0];
        if (!item) return '';
        return `<div class="QuotationMetrics_tooltip">
          <span class="QuotationMetrics_tooltipDot" style="background:${item.color}"></span>
          <span class="QuotationMetrics_tooltipLabel">${item.name}</span>
          <span class="QuotationMetrics_tooltipValue">${item.value}</span>
        </div>`;
      },
    },
  },
});

export default function QuotationMetricsNudge() {
  const userData = useSelector((state) => state.user);
  const [period, setPeriod] = useState('this_month');
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMetrics = async () => {
      if (!userData?.org) return;
      setLoading(true);
      try {
        const data = await fetchQuotationMetrics(userData.org, period);
        setMetricsData(data);
      } catch (err) {
        console.error('Failed to load quotation metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    loadMetrics();
  }, [userData?.org, period]);

  const chartData = useMemo(() => {
    if (!metricsData?.metrics) return [];
    return metricsData.metrics
      .filter((m) => m.count > 0)
      .map((m) => ({
        status: formatStatus(m.status),
        count: m.count,
      }));
  }, [metricsData]);

  const pieConfig = {
    data: chartData,
    angleField: 'count',
    colorField: 'status',
    radius: 0.85,
    innerRadius: 0.55,
    label: {
      text: 'count',
      style: { fontWeight: 'bold', fontSize: 12 },
    },
    legend: {
      color: {
        title: false,
        position: 'bottom',
        rowPadding: 5,
        itemMarker: 'circle',
        itemMarkerSize: 8,
      },
    },
    scale: {
      color: {
        domain: chartData.map((d) => d.status),
        range: chartData.map((d) => STATUS_COLOR_MAP[d.status?.toLowerCase()] || '#8C8C8C'),
      },
    },
    state: {
      inactive: { opacity: 0.5 },
    },
    ...buildPieTooltip(STATUS_COLOR_MAP),
  };

  return (
    <div className="QuotationMetrics_container">
      <div className="QuotationMetrics_header">
        <h5 style={{ color: ACCENT_COLOR }}>Deals Tracker</h5>
        <Select
          size="small"
          value={period}
          onChange={setPeriod}
          options={PERIOD_OPTIONS}
          className="QuotationMetrics_filter"
          popupMatchSelectWidth={false}
        />
      </div>
      <div className="QuotationMetrics_body">
        {loading ? (
          <div className="QuotationMetrics_loading">
            <Spin />
          </div>
        ) : chartData.length > 0 ? (
          <>
            <div className="QuotationMetrics_total">
              <span className="QuotationMetrics_totalCount" style={{ color: ACCENT_COLOR }}>
                {metricsData?.total ?? 0}
              </span>
              <span className="QuotationMetrics_totalLabel">Total</span>
            </div>
            <div className="QuotationMetrics_chart">
              <Pie {...pieConfig} />
            </div>
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No quotation data"
          />
        )}
      </div>
    </div>
  );
}
